import { useEffect, useState } from "react";
import type { MusicSource, MySongCandidate } from "@studio/shared";
import { ApiError, apiRequest } from "../lib/api";
import { InlineSpinner } from "./InlineSpinner";

/**
 * 「내 노래」 고르기(2026-10-16 아름학교 · jery 결정 4).
 *
 * 음악 수업과 그림·영상·포스터 수업이 서로 다른 수업방이라, 학생은 13자리 곡 코드를 손으로 옮겨 적어야 했다
 * (그날 그림·영상·포스터 작품 13개 중 노래를 붙인 것 2개). 서버가 «같은 선생님 + 같은 이름» 으로 찾은 곡을
 * 카드로 보여 주고, 누르면 바로 가져온다. 곡 코드 문자열은 서버가 내주지 않는다 — `track_id` 로 가져온다.
 * 🔑 후보가 없거나 목록을 못 읽으면 아무것도 그리지 않는다 — 아래의 코드 입력·파일 올리기가 그대로 있다.
 * 🔑 같은 이름의 다른 학생 곡이 섞일 수 있어 수업방 이름·앨범 그림을 같이 보여 준다.
 */

function durationLabel(seconds: number): string {
  const rounded = Math.max(0, Math.round(seconds));
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
}

function dateLabel(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

export function MySongPicker({
  workId,
  selectedTrackId,
  disabled = false,
  onBusyChange,
  onImported,
}: {
  workId: string;
  /** 지금 붙어 있는 노래의 원곡 id(`music_source.source_track_id`) — 그 카드에 「선택됨」 을 단다. */
  selectedTrackId?: string | null;
  disabled?: boolean;
  /**
   * 가져오는 동안 부모의 다른 버튼(건너뛰기·파일 올리기·다음)을 막는다 — 안 막으면 «노래 없이 건너뛰기» 뒤에
   * 늦게 끝난 가져오기가 노래를 다시 붙인다(Codex 검수 2026-10-16).
   */
  onBusyChange?: (busy: boolean) => void;
  onImported: () => Promise<unknown> | void;
}) {
  const [items, setItems] = useState<MySongCandidate[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void apiRequest<{ items: MySongCandidate[] }>(`/api/works/${encodeURIComponent(workId)}/song-candidates`)
      .then((result) => {
        if (active) setItems(result.items);
      })
      .catch(() => {
        if (active) setItems([]);
      });
    return () => {
      active = false;
    };
  }, [workId]);

  if (!items || items.length === 0) return null;

  const pick = async (item: MySongCandidate): Promise<void> => {
    setBusy(item.track_id);
    onBusyChange?.(true);
    setError(null);
    try {
      await apiRequest<{ music_source: MusicSource }>(`/api/works/${encodeURIComponent(workId)}/import-song`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ track_id: item.track_id }),
      });
      await onImported();
    } catch (caught) {
      setError(caught instanceof ApiError && caught.status === 429
        ? "잠깐 쉬었다가 다시 해요"
        : "이 노래를 가져오지 못했어요. 곡 코드로 가져와 주세요.");
    } finally {
      setBusy(null);
      onBusyChange?.(false);
    }
  };

  return (
    <section className="my-song-picker" aria-labelledby="my-song-picker-title">
      <h2 id="my-song-picker-title">내 노래에서 고르기</h2>
      <p>음악 만들기 수업에서 내 이름으로 만든 노래예요. 누르면 바로 붙어요.</p>
      <ul>
        {items.map((item) => {
          const selected = Boolean(selectedTrackId) && selectedTrackId === item.track_id;
          return (
            <li key={item.track_id}>
              <button
                type="button"
                className={`my-song-card${selected ? " is-selected" : ""}`}
                disabled={disabled || busy !== null}
                aria-pressed={selected}
                onClick={() => void pick(item)}
              >
                {item.cover_url
                  ? <img src={item.cover_url} alt="" loading="lazy" />
                  : <span className="my-song-card-note" aria-hidden="true">♪</span>}
                <span className="my-song-card-text">
                  <strong>{item.title}</strong>
                  <small>{item.session_title}</small>
                  <small>{[dateLabel(item.created_at), durationLabel(item.duration_seconds)].filter(Boolean).join(" · ")}</small>
                </span>
                <b>{busy === item.track_id ? <InlineSpinner label="붙이는 중..." /> : selected ? "선택됨 ✓" : "고르기"}</b>
              </button>
            </li>
          );
        })}
      </ul>
      {error ? <p className="my-song-picker-error" role="alert">{error}</p> : null}
    </section>
  );
}
